import { useEffect, useRef } from 'react'
import { useAppStore } from '@/store/appStore'
import { fetchBookmarks } from '@/api/bookmarksApi'
import { fetchNotes } from '@/api/notesApi'
import { fetchCustomViews } from '@/api/customViewsApi'
import { saveAllBookmarks, saveAllNotes, saveAllCustomViews } from '@/db/viewerDb'

/**
 * Runs once on app startup (when online) to fetch the current user's
 * bookmarks, notes, and custom views from the API and persist them to
 * IndexedDB so every consumer hook has fresh data from the cache.
 *
 * Individual hooks (useBookmarks, useNotes, useCustomViews) read from
 * IndexedDB on mount — they do NOT hit the API themselves — so without
 * this sync the cache would be empty on a fresh session.
 */
export function useInitialSync() {
  const { isOffline, bumpUserDataSyncVersion, setCustomViews, setPendingCustomViews } = useAppStore()
  const hasSynced = useRef(false)

  useEffect(() => {
    // Only run once, and only when online
    if (hasSynced.current || isOffline) return
    hasSynced.current = true

    const sync = async () => {
      try {
        const [bookmarksRes, notesRes, customViewsRes] = await Promise.allSettled([
          fetchBookmarks(),
          fetchNotes(),
          fetchCustomViews(),
        ])

        if (bookmarksRes.status === 'fulfilled') {
          await saveAllBookmarks(bookmarksRes.value.bookmarks)
        } else {
          console.warn('[InitialSync] bookmarks failed:', bookmarksRes.reason)
        }

        if (notesRes.status === 'fulfilled') {
          await saveAllNotes(notesRes.value.notes)
        } else {
          console.warn('[InitialSync] notes failed:', notesRes.reason)
        }

        if (customViewsRes.status === 'fulfilled') {
          await saveAllCustomViews(customViewsRes.value.customViews)
          // Also populate the Zustand store immediately
          setCustomViews(customViewsRes.value.customViews)
          setPendingCustomViews(customViewsRes.value.pendingCustomViews ?? [])
        } else {
          console.warn('[InitialSync] custom views failed:', customViewsRes.reason)
        }

        // Notify consumers (useNotes, useBookmarks, etc.) to re-read from IndexedDB
        bumpUserDataSyncVersion()
      } catch (err) {
        // Non-fatal — consumers will fall back to whatever is in the cache
        console.warn('[InitialSync] unexpected error:', err)
      }
    }

    sync()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
